import { BUYER } from "codes";

type OnboardingStatusLike = {
  documents?: { status?: unknown };
  details?: { status?: unknown };
  users?: { status?: unknown };
  bank_details?: { status?: unknown };
};

export const isOnboardingStepComplete = (status?: unknown): boolean => {
  if (typeof status === "boolean") {
    return status;
  }
  if (typeof status === "number") {
    return status === 1;
  }
  if (typeof status === "string") {
    return ["true", "complete", "completed", "done", "1"].includes(
      status.toLowerCase(),
    );
  }
  return false;
};

export const getOnboardingStepFlags = (
  onboardingStatus?: OnboardingStatusLike | null,
) => ({
  isDocumentsStepComplete: isOnboardingStepComplete(
    onboardingStatus?.documents?.status,
  ),
  isDetailsStepComplete: isOnboardingStepComplete(
    onboardingStatus?.details?.status,
  ),
  isUsersStepComplete: isOnboardingStepComplete(
    onboardingStatus?.users?.status,
  ),
  isBankDetailsStepComplete: isOnboardingStepComplete(
    onboardingStatus?.bank_details?.status,
  ),
});

export const isDocumentsMandatoryComplete = ({
  isDocumentsStepComplete,
  pendingDocsComplete,
  areRequiredDocumentsUploaded,
}: {
  isDocumentsStepComplete: boolean;
  pendingDocsComplete?: boolean;
  areRequiredDocumentsUploaded: boolean;
}): boolean =>
  Boolean(
    isDocumentsStepComplete ||
      pendingDocsComplete ||
      areRequiredDocumentsUploaded,
  );

const buyerProfile = BUYER.toLowerCase();

/** Own-company profile: buyers skip bank details; sellers require them. */
export const isCompanyProfileComplete = ({
  isDetailsStepComplete,
  isUsersStepComplete,
  isBankDetailsStepComplete,
  normalizedProfile,
}: {
  isDetailsStepComplete: boolean;
  isUsersStepComplete: boolean;
  isBankDetailsStepComplete: boolean;
  normalizedProfile: string;
}): boolean =>
  isDetailsStepComplete &&
  isUsersStepComplete &&
  (normalizedProfile === buyerProfile || isBankDetailsStepComplete);

/**
 * Partner profile as seen by the viewer: buyers require the partner's bank
 * details; sellers do not.
 */
export const isPartnerProfileComplete = ({
  isDetailsStepComplete,
  isUsersStepComplete,
  isBankDetailsStepComplete,
  normalizedProfile,
}: {
  isDetailsStepComplete: boolean;
  isUsersStepComplete: boolean;
  isBankDetailsStepComplete: boolean;
  normalizedProfile: string;
}): boolean =>
  Boolean(
    isDetailsStepComplete &&
      isUsersStepComplete &&
      (normalizedProfile !== buyerProfile || isBankDetailsStepComplete),
  );

export const isOnboardingReadyToSubmit = ({
  isProfileComplete,
  isTermsheetSigned,
  documentsMandatoryComplete,
  isDocumentsReviewed,
}: {
  isProfileComplete: boolean;
  /** When omitted (My Company), termsheet is not required for Submit. Partners still pass it. */
  isTermsheetSigned?: boolean;
  documentsMandatoryComplete: boolean;
  isDocumentsReviewed: boolean;
}): boolean =>
  Boolean(
    isProfileComplete &&
      (isTermsheetSigned === undefined || isTermsheetSigned) &&
      documentsMandatoryComplete &&
      isDocumentsReviewed,
  );

/** Client contacts must have role-specific docs (Director ID, LOA, etc.) uploaded. */
export const isClientUsersReadyForSubmit = ({
  isUsersStepComplete,
  hasClientContacts,
  isClientUsersDocumentsComplete,
  isCheckingClientUserDocuments,
}: {
  isUsersStepComplete: boolean;
  hasClientContacts: boolean;
  isClientUsersDocumentsComplete: boolean;
  isCheckingClientUserDocuments: boolean;
}): boolean =>
  Boolean(
    isUsersStepComplete &&
      hasClientContacts &&
      isClientUsersDocumentsComplete &&
      !isCheckingClientUserDocuments,
  );

/** Trade partner users: backend onboarding users step only — no contact document gate. */
export const isPartnerUsersReadyForSubmit = ({
  isUsersStepComplete,
  hasContacts,
}: {
  isUsersStepComplete: boolean;
  hasContacts: boolean;
}): boolean => Boolean(isUsersStepComplete && hasContacts);
